import { useState } from "react";
import { Link } from "wouter";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RatesNote } from "./RatesNote";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseNumber } from "./numberInput";
import { amountError, pretaxError, K401_AGE_LABEL, k401LimitFor, type K401Age } from "./calcChecks";
import { FILING_LABEL, US_TAX_YEAR, type Filing } from "./usTax";
import { STATES, stateRateLabel, statePayrollMissingNote } from "./stateTaxData";
import { computeIncomeTax, perPeriod, INCOME_PERIOD_LABEL, type IncomePeriod } from "./incomeTaxCalc";

const TOOL_INFO_CONTENT = {
  what: `Estimates your US federal income tax, payroll (FICA) or self-employment tax, state income tax and California State Disability Insurance (SDI) for ${US_TAX_YEAR}, using the official brackets and standard deduction for your filing status. A Custom Flat Rate mode is also available for a quick estimate with any tax rate and currency outside the US.`,
  how: `Enter your income and filing status, choose employee or self-employed, and pick your state (or type your own state rate). Add pre-tax contributions such as a traditional 401(k) to see how they lower your tax. Choose how to see the result: per year, month or paycheck (semi-monthly, biweekly, weekly). Switch to Custom Flat Rate to enter a percentage directly — the UK Tax Calculator and India Income Tax Calculator handle those systems properly.`,
  tip: `This uses the standard deduction and ignores credits (such as the child tax credit) — if you itemize or have credits, your actual liability could differ meaningfully.`,
};

const CURRENCY_OPTIONS = [
  { symbol: "$", label: "$ (USD)" }, { symbol: "€", label: "€ (EUR)" }, { symbol: "£", label: "£ (GBP)" }, { symbol: "₹", label: "₹ (INR)" },
  { symbol: "¥", label: "¥ (JPY/CNY)" }, { symbol: "A$", label: "A$ (AUD)" }, { symbol: "C$", label: "C$ (CAD)" },
];

const n0 = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });
const SEL = "border rounded px-2 py-2 w-full bg-background text-sm";

export default function IncomeTaxCalculator() {
  const [mode, setMode] = useState<"us-federal" | "custom">("us-federal");
  const [income, setIncome] = useState("85000");
  const [filing, setFiling] = useState<Filing>("single");
  const [stateCode, setStateCode] = useState("NONE");
  const [stateRate, setStateRate] = useState("5");
  const [isSelfEmployed, setIsSelfEmployed] = useState(false);
  const [pretax, setPretax] = useState("0");
  const [k401Age, setK401Age] = useState<K401Age>("under50");
  const [customRate, setCustomRate] = useState("20");
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [period, setPeriod] = useState<IncomePeriod>("year");

  const gross = Math.max(0, parseNumber(income) ?? 0);
  const incomeErr = amountError(income, "an income");
  const { blocked } = useLogToolUsage(true, 3000);

  const tabs = (
    <div className="flex gap-2">
      <button onClick={() => setMode("us-federal")} className={`text-sm px-3 py-1.5 rounded-md border ${mode === "us-federal" ? "bg-primary/10 border-primary/30 font-medium" : "bg-card"}`}>US Federal Brackets</button>
      <button onClick={() => setMode("custom")} className={`text-sm px-3 py-1.5 rounded-md border ${mode === "custom" ? "bg-primary/10 border-primary/30 font-medium" : "bg-card"}`}>Custom Flat Rate</button>
    </div>
  );

  if (mode === "custom") {
    const rate = parseNumber(customRate) ?? 0;
    const customTax = gross * (rate / 100);
    return (
      <ToolContainer>
        <div className="max-w-md mx-auto space-y-6">
          {tabs}
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Currency</Label>
              <select value={currencySymbol} onChange={(e) => setCurrencySymbol(e.target.value)} className="w-full h-10 rounded-md border bg-background px-3 text-sm">
                {CURRENCY_OPTIONS.map((c) => <option key={c.symbol} value={c.symbol}>{c.label}</option>)}
              </select>
            </div>
            <div><Label>Annual Income</Label><Input inputMode="decimal" aria-label="Annual income" aria-invalid={!!incomeErr} value={income} onChange={(e) => setIncome(e.target.value)} />{incomeErr && <p role="alert" className="text-xs text-destructive mt-1">{incomeErr}</p>}</div>
          </div>
          <div><Label>Your Tax Rate (%)</Label><Input aria-label="Your Tax Rate (%)" inputMode="decimal" value={customRate} onChange={(e) => setCustomRate(e.target.value)} /></div>
          {incomeErr ? null : blocked ? (
            <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>
          ) : (
            <>
              <div className="p-4 rounded-xl border bg-primary/5 text-center">
                <p className="text-xs text-muted-foreground">Estimated Take-Home Income</p>
                <p className="text-2xl font-bold text-primary">{currencySymbol}{n0(gross - customTax)}</p>
              </div>
              <div className="text-center p-3 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground">Tax at {rate}%</p>
                <p className="text-sm font-bold">{currencySymbol}{n0(customTax)}</p>
              </div>
            </>
          )}
          <p className="text-xs text-muted-foreground text-center">
            A simple flat-rate estimate using the percentage you entered — it doesn't apply any country's actual bracket structure, deductions or social contributions. For an accurate US, UK or India calculation use the US Federal Brackets mode or the dedicated UK/India calculators.
          </p>
        </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
      </ToolContainer>
    );
  }

  const preErr = pretaxError(pretax, gross, !isSelfEmployed, k401Age);
  const inputErr = incomeErr || preErr;
  const pre = Math.min(Math.max(0, parseNumber(pretax) ?? 0), gross);
  const { std, taxable, fedTax, payroll, stateAmt, stateRes, statePayroll, totalTax, net, effective, marginal, qbi, se, fica } = computeIncomeTax({
    gross, pre, filing, selfEmployed: isSelfEmployed, stateCode, customStatePct: parseNumber(stateRate) ?? 0,
  });

  const per = (n: number) => `$${n0(perPeriod(n, period))}`;
  const periodWord = period === "year" ? "year" : period === "month" ? "month" : "paycheck";
  const Box = ({ label, value, id }: { label: string; value: string; id?: string }) => (
    <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-bold" data-testid={id}>{value}</p></div>
  );

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        {tabs}
        <div><Label>{isSelfEmployed ? "Net Self-Employment Profit" : "Annual Gross Income"} ($, {US_TAX_YEAR})</Label>
          <Input inputMode="decimal" aria-label="Annual income" aria-invalid={!!incomeErr} value={income} onChange={(e) => setIncome(e.target.value)} />{incomeErr && <p role="alert" className="text-xs text-destructive mt-1">{incomeErr}</p>}</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Filing status</Label>
            <select className={SEL} aria-label="Filing status" value={filing} onChange={(e) => setFiling(e.target.value as Filing)}>
              {(Object.keys(FILING_LABEL) as Filing[]).map((k) => <option key={k} value={k}>{FILING_LABEL[k]}</option>)}
            </select></div>
          <div><Label className="text-xs">State</Label>
            <select className={SEL} aria-label="State" value={stateCode} onChange={(e) => setStateCode(e.target.value)}>
              <option value="NONE">No state</option>
              <option value="CUSTOM">Custom rate…</option>
              {STATES.map((s) => <option key={s.code} value={s.code}>{s.name}{s.kind === "none" ? " (no tax)" : ""}</option>)}
            </select></div>
        </div>
        {stateCode === "CUSTOM" && (
          <div><Label className="text-xs">State tax rate (% of income, 0 if none)</Label><Input inputMode="decimal" aria-label="State tax rate" value={stateRate} onChange={(e) => setStateRate(e.target.value)} /></div>
        )}
        <div><Label className="text-xs">Pre-tax contributions ($/year, e.g. traditional 401(k))</Label><Input inputMode="decimal" aria-label="Pre-tax contributions" aria-invalid={!!preErr} value={pretax} onChange={(e) => setPretax(e.target.value)} />{preErr && <p role="alert" className="text-xs text-destructive mt-1">{preErr}</p>}</div>
        <div><Label className="text-xs">Show amounts per</Label>
          <select className={SEL} aria-label="Show amounts per" value={period} onChange={(e) => setPeriod(e.target.value as IncomePeriod)}>
            {(Object.keys(INCOME_PERIOD_LABEL) as IncomePeriod[]).map((k) => <option key={k} value={k}>{INCOME_PERIOD_LABEL[k]}</option>)}
          </select></div>
        {!isSelfEmployed && (
          <div><Label className="text-xs">Age group for the 401(k) limit (2026: ${n0(k401LimitFor(k401Age))})</Label>
            <select className={SEL} aria-label="Age group for the 401(k) limit" value={k401Age} onChange={(e) => setK401Age(e.target.value as K401Age)}>
              {(Object.keys(K401_AGE_LABEL) as K401Age[]).map((k) => <option key={k} value={k}>{K401_AGE_LABEL[k]}</option>)}
            </select>
            {k401Age !== "under50" && gross > 150000 && <p className="text-xs text-muted-foreground mt-1">Catch-up contributions are Roth (after-tax) if your prior-year Social Security wages were above about $150,000, so the part above ${n0(k401LimitFor("under50"))} would not lower your taxable income.</p>}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input type="checkbox" id="self-employed" checked={isSelfEmployed} onChange={(e) => setIsSelfEmployed(e.target.checked)} className="h-4 w-4" />
          <label htmlFor="self-employed" className="text-sm">I'm self-employed (self-employment tax on 92.35% of profit, half deductible)</label>
        </div>

        {inputErr ? null : blocked ? (
          <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>
        ) : (
          <>
            <div className="p-4 rounded-xl border bg-primary/5 text-center">
              <p className="text-xs text-muted-foreground">Estimated take-home income per {periodWord}</p>
              <p className="text-2xl font-bold text-primary" data-testid="it-net">{per(net)}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Box label="Federal income tax" value={per(fedTax)} id="it-fed" />
              <Box label={isSelfEmployed ? "Self-employment tax" : "FICA (SS + Medicare)"} value={per(payroll)} id="it-payroll" />
              <Box label={stateCode === "NONE" ? "State income tax (none selected)" : stateRes ? "State income tax" : "State tax (custom rate)"} value={per(stateAmt)} id="it-state" />
              {statePayroll && <Box label={`${statePayroll.label} (${statePayroll.ratePct}% of wages)`} value={per(statePayroll.amount)} id="it-sdi" />}
              <Box label="Total tax" value={per(totalTax)} id="it-total" />
              <Box label="Effective rate" value={`${effective.toFixed(1)}%`} />
              <Box label="Federal marginal rate" value={taxable > 0 ? `${marginal.toFixed(0)}%` : "—"} />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Taxable income {`$${n0(taxable)}`} after the ${n0(std)} standard deduction{pre > 0 ? ` and $${n0(pre)} pre-tax contributions` : ""}{se ? ` and the $${n0(se.deductibleHalf)} deductible half of self-employment tax` : ""}{qbi && qbi.amount > 0 ? ` and the $${n0(qbi.amount)} qualified business income deduction` : ""}.
              {fica && fica.additionalMedicare > 0 ? " Includes 0.9% Additional Medicare Tax." : ""}
            </p>
            {stateRes && <p className="text-xs text-muted-foreground text-center" data-testid="it-state-source">{stateRateLabel(stateCode)}</p>}
            {statePayroll && <p className="text-xs text-muted-foreground text-center" data-testid="it-sdi-note">{statePayroll.note} It is taken from gross wages, so a 401(k) contribution does not lower it.</p>}
            {!isSelfEmployed && statePayrollMissingNote(stateCode) && <p className="text-xs text-muted-foreground text-center" data-testid="it-payroll-missing">{statePayrollMissingNote(stateCode)}</p>}
            {isSelfEmployed && stateCode === "CA" && <p className="text-xs text-muted-foreground text-center">California SDI is withheld from employee wages only, so it is not applied to self-employment profit.</p>}
            {isSelfEmployed && <p className="text-xs text-center">See the <Link href="/tool/self-employment-tax" className="underline text-primary">Self-Employment Tax Calculator</Link> for quarterly payments and W-2 wages.</p>}
          </>
        )}
        <p className="text-xs text-muted-foreground text-center">
          Estimate only, based on {US_TAX_YEAR} US federal brackets and the standard deduction for the selected filing status; credits and itemised deductions are not included. Only California's payroll tax (SDI) is added; other states' disability and paid-leave contributions are not. State figures are estimates (see the note under the result for each state's tax year and source). Not tax advice — consult a tax professional.
        </p>
      </div>
      <RatesNote id="usFederal" />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
